<template>
	<div
		v-if="setup.tempWorker.id"
		class="temp-worker-aside-details">
		<div class="temp-worker-aside-details__left">
			<div class="temp-worker-aside-details__header">
				<o-avatar
					v-if="setup.self.isAgency()"
					:name="setup.tempWorker.firstName"
					size="sm"
					:avatar="setup.tempWorker.avatarId"
					root-url="/app/avatar/"/>
				<h1 v-if="setup.self.isAgency()">Profil de <strong>{{ setup.tempWorker.name }}</strong></h1>
				<h1 v-else-if="setup.self.isClient()">Profil de <strong>{{ setup.tempWorker.namePartial }}</strong></h1>
			</div>

			<div
				v-if="!setup.tempWorker.profileIsPublic"
				class="flex fd-c g-xs">
				<o-alert
					color="warning"
					center>
					L'interimaire ne partage pas son profil
				</o-alert>

				<o-button
					v-if="setup.isForAgency()"
					v-tooltip="setup.tempWorker.requestPublicProfileDate
						? `Demande envoyée le ${setup.tempWorker.requestPublicProfileDate.toReadable('$DD/$MM/$YYYY à $hhh$mm')}`
						: undefined"
					color="brand"
					@click="setup.tempWorker.requestForPublicProfileAsync()">
					Demander le partage du profil
				</o-button>
			</div>

			<o-tabs
				v-else
				v-model="setup.activeTab"
				:class="{ 'orion-tabs--hide' : setup.self.isClient() }"
				align="left">
				<o-tab-pane
					label="Général"
					name="general">
					<!-- Intro & Coordonnées -->
					<div class="temp-worker-aside-details__section temp-worker-aside-details__section--a-propos">
						<o-section title="À Propos">
							<p
								v-if="setup.tempWorker.introduction"
								class="text--pre-line">
								{{ setup.tempWorker.introduction }}
							</p>
							<o-alert
								v-else
								color="default">
								Aucune introduction n'a été renseignée.
							</o-alert>
						</o-section>

						<o-section
							v-if="setup.self.isAgency() && setup.isForAgency()"
							title="Informations">
							<div class="flex fd-c g-xs">
								<div
									v-if="setup.tempWorker.email"
									class="flex fd-c">
									<strong>Email</strong>
									<span>{{ setup.tempWorker.email }}</span>
								</div>
								<div
									v-if="setup.tempWorker.mobilePhone"
									class="flex fd-c">
									<strong>Téléphone</strong>
									<span>{{ setup.tempWorker.mobilePhoneFormatted }}</span>
								</div>
								<div
									v-if="setup.tempWorker.city"
									class="flex fd-c">
									<strong>Adresse</strong>
									<span>{{ setup.tempWorker.address }}</span>
								</div>
								<div
									v-if="setup.tempWorker.birthDay"
									class="flex fd-c">
									<strong>Date de naissance</strong>
									<span>{{ setup.tempWorker.birthDay.toReadable() }}</span>
								</div>
								<div
									v-if="setup.tempWorker.createdDate"
									class="flex fd-c">
									<strong>Date de création</strong>
									<span>{{ setup.tempWorker.createdDate.toReadable() }}</span>
								</div>
								<div
									v-if="setup.tempWorker.activationDate"
									class="flex fd-c">
									<strong>Date d'activation</strong>
									<span>{{ setup.tempWorker.activationDate.toReadable() }}</span>
								</div>
							</div>
						</o-section>
					</div>

					<hr>

					<!-- Qualifications -->
					<div class="temp-worker-aside-details__section">
						<o-section title="Qualifications">
							<o-alert
								v-if="!!order && !setup.tempWorker.hasRequiredOrderProfessionalSkill(order) && setup.self.isAgency()"
								color="warning"
								class="temp-worker-aside-details__warning">
								Attention, le candidat ne possède pas la qualification requise de la commande :
								<b>{{ order.professionalSkill?.label }}</b>
							</o-alert>
							<div
								v-if="setup.tempWorker.professionalSkills.length"
								class="temp-worker-aside-details__skills">
								<o-chips
									v-for="skill in setup.tempWorker.professionalSkills"
									:key="skill.id"
									:color="(!!order && setup.orderService.isOrderProfessionalSkill(order, skill.id)) ? 'info' : 'default'"
									outline>
									{{ skill.label }}
								</o-chips>
							</div>
							<o-alert
								v-else
								color="default">
								Aucune qualification n'a été renseignée
							</o-alert>
						</o-section>
					</div>

					<!-- Qualifications de l'agence -->
					<template v-if="setup.self.isAgency() && setup.isForAgency()">
						<hr>

						<div class="temp-worker-aside-details__section">
							<o-section title="Qualifications de l'agence">
								<div
									v-if="setup.tempWorker.userAgencyQualifications.length"
									class="temp-worker-aside-details__agency-skills">
									<o-chips
										v-for="qualification in setup.tempWorker.userAgencyQualifications"
										:key="qualification.qualificationCode"
										outline>
										<div class="flex ai-c g-xs">
											{{ qualification.qualificationLabel }}
											<span>-</span>
											<span>{{ qualification.agencyCompanyName }}</span>
											<em>{{ qualification.agencyCompanyContactDetailCity }}</em>
										</div>
									</o-chips>
								</div>
								<o-alert
									v-else
									color="default">
									Aucune qualification de l'agence n'a été renseignée
								</o-alert>
							</o-section>
						</div>
					</template>

					<hr>

					<!-- Langues -->
					<div class="temp-worker-aside-details__section">
						<o-section title="Langues">
							<div
								v-if="setup.tempWorker.userLanguages.length"
								class="temp-worker-aside-details__langages">
								<o-chips
									v-for="lang in setup.tempWorker.userLanguages"
									:key="lang.id"
									outline>
									{{ lang.language }}
									<template #dual>
										{{ setup.userLanguageService.getReadableLanguageLevel(lang.languageLevel) }}
									</template>
								</o-chips>
							</div>
							<o-alert
								v-else
								color="default">
								Aucune langue n'a été renseignée.
							</o-alert>
						</o-section>
					</div>

					<hr>

					<!-- Expériences Pro -->
					<div class="temp-worker-aside-details__section">
						<o-section
							class="temp-worker-aside-details__experiences"
							title="Expériences">
							<div v-if="setup.tempWorker.orderedProfessionalExperiences.length">
								<div
									v-for="experience in setup.tempWorker.orderedProfessionalExperiences"
									:key="experience.id"
									class="temp-worker-aside-details__experiences-sticker">
									<div class="temp-worker-aside-details__experiences-sticker-section">
										<o-icon font-icon="icon-shopping-building"/>
										<div>
											<strong>{{ experience.company }}</strong><br>
											{{ experience.position }}
										</div>
									</div>
									<div class="temp-worker-aside-details__experiences-sticker-section">
										<o-icon font-icon="icon-interface-calendar-mark"/>
										<div v-if="experience.endDate">
											Du <strong>{{ experience.startDate.toReadableDate() }}</strong> <br>
											Au <strong>{{ experience.endDate.toReadableDate() }}</strong>
										</div>
										<div v-else>
											En poste depuis <br>
											le <strong>{{ experience.startDate.toReadableDate() }}</strong> <br>
										</div>
									</div>
								</div>
							</div>
							<o-alert
								v-else
								color="default">
								Aucune expérience n'a été renseignée.
							</o-alert>
						</o-section>
					</div>

					<hr>

					<!-- Documents -->
					<div
						v-if="setup.self.isAgency() && setup.isForAgency()"
						class="temp-worker-aside-details__section">
						<o-section
							class="temp-worker-aside-details__documents"
							title="Documents justificatifs">
							<o-alert
								v-if="order && setup.tempWorker.missingJustificatories.length"
								color="warning"
								class="temp-worker-aside-details__warning">
								Attention, le candidat ne possède pas certains documents requis de la commande :
								<b>{{ setup.tempWorker.missingJustificatories.filter(x => x.marker).map(x => x.label).join(' , ') }}</b>
							</o-alert>
							<div
								v-if="setup.tempWorker.justificatories.length"
								class="temp-worker-aside-details__documents-section">
								<shared-picto-card
									v-for="doc in setup.tempWorker.justificatories"
									:key="`${doc.tooltip}--${doc.id}`"
									:marker="doc.marker"
									:color="setup.tempWorker.missingJustificatories
										.filter(x => x.marker)
										.mapKey('tempWorkerDocumentType')
										.includes(doc.tempWorkerDocumentType)
										? 'warning'
										: doc.color"
									:picto="doc"
									label/>
							</div>
							<o-alert
								v-else
								color="default">
								Aucun justificatif n'a été renseigné.
							</o-alert>
						</o-section>

						<o-section
							class="temp-worker-aside-details__documents"
							title="Diplômes">
							<div
								v-if="setup.tempWorker.diplomas.length"
								class="temp-worker-aside-details__documents-section">
								<shared-picto-card
									v-for="doc in setup.tempWorker.diplomas"
									:key="doc.id"
									:color="doc.color"
									:picto="doc"
									label/>
							</div>
						</o-section>

						<o-section
							class="temp-worker-aside-details__documents"
							title="Documents personnalisés">
							<div
								v-if="setup.tempWorker.customDocuments.length"
								class="temp-worker-aside-details__documents-section">
								<shared-picto-card
									v-for="doc in setup.tempWorker.customDocuments"
									:key="doc.id"
									:color="doc.color"
									:picto="doc"
									label/>
							</div>
							<o-alert
								v-else
								color="default">
								Aucun document personnalisé n'a été renseigné.
							</o-alert>
						</o-section>
						<hr>
					</div>

					<div
						v-if="setup.self.isAgency() && setup.isForAgency()"
						class="temp-worker-aside-details__section">
						<o-section
							class="temp-worker-aside-details__documents"
							title="Moyen de locomotion">
							<o-alert
								v-if="order && order.needPersonalVehicle && !setup.tempWorker.hasPersonalVehicle"
								color="warning">
								Attention, le candidat ne possède pas de véhicule personnel
							</o-alert>
							<div
								v-else-if="setup.tempWorker.hasPersonalVehicle"
								class="temp-worker-aside-details__vehicle-info">
								<o-icon font-icon="icon-travel-transportation-car-1"/>
								<div>
									<strong>Le candidat possède un véhicule personnel</strong> <br>
									<span>Sur une distance de {{ setup.tempWorker.mobility }}</span>
								</div>
							</div>
						</o-section>
					</div>
				</o-tab-pane>

				<o-tab-pane
					v-if="setup.self.isAgency() && setup.isForAgency()"
					label="Disponibilités"
					name="disponibility"
					marker-color="warning"
					:marker="!!setup.tempWorker.unavailabilities?.length">
					<temp-worker-availability
						:temp-worker="setup.tempWorker"
						readonly/>
				</o-tab-pane>
			</o-tabs>
		</div>

		<div class="temp-worker-aside-details__right">
			<div
				v-if="setup.self.isClient()
					&& !orderClientProposal?.responseDate
					&& setup.self.can('OrderCreate')
					&& setup.self.can('OrderCandidateUpdate')
					&& !isValidatedCandidate"
				class="temp-worker-aside-details__section temp-worker-aside-details__section-button">
				<o-button
					prefix-font-icon="icon-interface-validation-check"
					color="success"
					@click="setup.acceptCandidateAsync()">
					Accepter le candidat
				</o-button>
				<o-button
					prefix-font-icon="icon-interface-delete-1"
					color="danger"
					outline
					@click="setup.declineCandidateAsync()">
					Refuser le candidat
				</o-button>
			</div>

			<!-- <div
				v-if="$isClient && candidate.responseDate && $self.can('OrderCreate')"
				class="temp-worker-aside-details__section temp-worker-aside-details__section-button">
				<o-alert :color="clientProposal.color">
					{{ clientProposal.label }}
				</o-alert>
			</div> -->

			<template v-if="setup.self.isAgency() && setup.isForAgency()">
				<div class="temp-worker-aside-details__section">
					<temp-worker-rating
						v-bind="{
							numberOfRating: setup.tempWorker.numberOfRating,
							rating: setup.tempWorker.rating,
							authorRating: setup.tempWorker.authorUserRating,
							targetRatingId: setup.tempWorker.id,
						}"/>
				</div>

				<div class="temp-worker-aside-details__section">
					<o-section title="Vivier">
						<shared-order-group-select
							:value="setup.tempWorker.talentPoolIds"
							:candidate-id="setup.tempWorker.id"/>
					</o-section>
				</div>

				<div
					:ref="setup._refs.taskSection"
					class="temp-worker-aside-details__section">
					<temp-worker-task-list
						:task-list="setup.tempWorker.userTasks"
						:task-type="1"
						:temp-worker="setup.tempWorker"/>
				</div>
			</template>

			<div
				:ref="setup._refs.commentSection"
				class="temp-worker-aside-details__section">
				<temp-worker-comment-list
					:comment-list="setup.tempWorker.userProfileComments"
					:candidate-id="setup.tempWorker.id"
					:comment-type="1"/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import TempWorkerAsideDetailsSetupService from '@/setup/temp-worker/TempWorkerAsideDetailsSetupService';
import TempWorkerAvailability from './TempWorkerAvailability.vue';
import TempWorkerCommentList from './TempWorkerCommentList.vue';
import TempWorkerTaskList from './TempWorkerTaskList.vue';
import TempWorkerRating from './TempWorkerRating.vue';
import SharedPictoCard from '../shared/SharedPictoCard.vue';
import { inject } from 'vue';
const _aside = inject<OrionAside>('_aside');
const props = defineProps(TempWorkerAsideDetailsSetupService.props);
const setup = new TempWorkerAsideDetailsSetupService(props, _aside);
</script>

<style scoped lang="less">
@import '@/styles/variables.less';

@asideFixedWidth: ~'screen and (min-width: 66rem)';

.empty-text {
  font-style: italic;
  opacity: 0.5;
}

.temp-worker-aside-details {
  min-height: calc(100vh - 2rem);

	:deep(.orion-alert) {
		margin-bottom: 1rem;
	}

  :deep(.orion-section__header) {
    min-height: 2.5rem;
  }

	.orion-tabs--hide {
		:deep(.orion-tabs__header) {
			display: none;
		}
	}

	@media @asideFixedWidth {
		margin: calc(var(--fluid-60px) * -1);
		display: grid;
		grid-template-columns: 5fr 3fr;
	}

  &__left {
    @media @asideFixedWidth {
			padding: var(--fluid-40px);
		}
  }

  &__right {
    background: var(--grey-lighter);
		margin: 0 calc(var(--fluid-30px) * -1) calc(var(--fluid-30px) * -1) calc(var(--fluid-30px) * -1);
    padding: var(--fluid-60px) var(--fluid-40px);

		@media @tablet {
			margin: 0 calc(var(--fluid-60px) * -1) calc(var(--fluid-60px) * -1);
		}

		@media @asideFixedWidth {
			margin: unset;
			margin-bottom: calc(var(--fluid-60px) * -1);
      padding: var(--fluid-60px) var(--fluid-40px);
		}

    .orion-card,
    .orion-sticker {
      box-shadow: none;
    }

		.orion-rate {
			&__number {
				order: -1;
				margin-right: 5px;
			}
		}

    .temp-worker-aside-details__section {
      margin-bottom: 3rem;

			&-button {
				display: flex;
				flex-direction: column;
				gap: 1rem;
			}
    }
  }

  &__header {
    position: relative;
    // display: flex;
    gap: 15px;
    align-items: center;
    margin-bottom: 50px;

		@media @tablet {
			display: flex;
		}

    h1 {
      font-size: 2rem;
      font-weight: 200;
      margin-bottom: 0;

      strong {
        font-weight: 400;
      }
    }
  }

  &__section {
    color: var(--grey-dark);

    &--a-propos {
      display: flex;
      gap: var(--space-md);

      @media @phone {
        flex-direction: column;
      }

      .orion-section {
        margin: 0;

        &:first-child {
          flex: 2;
        }

        &:last-child {
          flex: 1;
        }
      }
    }
  }

  &__skills, &__langages {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-xs);
  }

  &__agency-skills {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-xs);
  }

  &__experiences {
    &-sticker {
      border-radius: 0.5rem;
      background: var(--grey-lighter);
      display: flex;
			flex-direction: column;
      gap: 1.25rem;
      padding: 1rem 1.25rem;
      margin-bottom: 0.5rem;

			@media @tablet {
				flex-direction: row;
			}

      &-section {
        flex: 1;
        display: flex;
        align-items: center;

        .orion-icon {
          font-size: 2rem;
          margin-right: 1rem;
          color: var(--grey);
        }
      }
    }
  }

  &__documents-section {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(4.5rem, 1fr));
    grid-auto-rows: 1fr;
		gap: 1rem;
  }

	&__vehicle-info {
		border-radius: 0.25rem;
		background: var(--grey-lighter);
		padding: 1rem 1.25rem;
		display: flex;
		align-items: center;

		.orion-icon {
			font-size: 2rem;
			color: var(--grey);
			margin-right: 1.25rem;
		}
	}
}
</style>
